import { useGetBusinessReportsQuery, useGetTemplateFavoritesQuery } from "../businessReports.api";
import { BusinessReport, BusinessReportPrompt } from "@/modules/snapshots/businessReports.types";
import * as React from "react";
import { ClipboardList } from "lucide-react";
import { BusinessReportsPageGenerate } from "./generate";
import { BusinessReportRow } from "./businessReportRow";
import { BusinessReportDetails } from "./businessReportDetails";
import { useParams, useSearchParams } from "react-router";
import { ReportsLayoutContent } from "../generate/layout";
import { useGetFileDetailsQuery } from "@/api/files";
import { flatFileSystem } from "@/components/feature/layout/left-sidebar/helpers";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { useGroupedTemplates } from "@/modules/snapshots/hooks/useGroupedTemplates";

const emptyList: BusinessReport[] = [];
const emptyFavoriteIds = new Set<string>();

export const BusinessReportGenerateView: React.FC = () => (
  <ReportsLayoutContent>
    <BusinessReportGenerate />
  </ReportsLayoutContent>
);
export const BusinessReportGenerate: React.FC = () => {
  const { data: businessReports = emptyList } = useGetBusinessReportsQuery({ type: "snapshot_template" });
  const { data: favorites = [] } = useGetTemplateFavoritesQuery();
  const [prompts, setPrompts] = React.useState<BusinessReportPrompt[]>([]);
  const { fseId } = useParams<{ fseId: string }>();
  const { currentData } = useGetFileDetailsQuery({ fse_id: fseId, depth: 50 });
  const [searchParams] = useSearchParams();
  const { setSelectedFiles } = useCurrentPdf();
  const businessReportId = searchParams.get("businessReport");

  const currentResource = currentData?.id === fseId ? currentData : undefined;
  const fsEntries = React.useMemo(
    () => (currentResource ? flatFileSystem(currentResource, 50, new Set(), false) : []),
    [currentResource],
  );

  React.useEffect(() => {
    setSelectedFiles(fsEntries.map(row => row.id));
  }, [fsEntries, setSelectedFiles]);

  const [selectedBusinessReport, setSelectedBusinessReport] = React.useState<BusinessReport | null>(null);

  const handleTemplateSelect = (businessReport: BusinessReport) => {
    setSelectedBusinessReport(businessReport);
  };

  React.useEffect(() => {
    if (businessReportId) {
      const selectedBusinessReport = businessReports.find(businessReport => businessReport.id === businessReportId);
      if (selectedBusinessReport) {
        setSelectedBusinessReport(selectedBusinessReport);
      }
    }
  }, [businessReports, businessReportId]);

  React.useEffect(() => {
    if (selectedBusinessReport) {
      setPrompts(selectedBusinessReport.prompts);
    }
  }, [selectedBusinessReport]);

  const favoriteIds = React.useMemo(() => new Set(favorites.map(f => f.business_report_id)), [favorites]);
  const groupedTemplates = useGroupedTemplates(businessReports, favoriteIds.size > 0 ? favoriteIds : emptyFavoriteIds);
  return (
    <div className="flex flex-grow flex-col overflow-y-auto bg-[#f7f3ee] px-8 pb-7 pt-7">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
        <div className="min-w-0">
          <p className="mb-2 text-sm font-semibold text-gray-600">Snapshots</p>
          <h1 className="truncate text-3xl font-bold leading-tight text-[#111b35]">New SnapShot</h1>
          <p className="mt-1 text-sm font-medium text-gray-500">
            Choose a template to run against the selected documents.
          </p>
        </div>
        {selectedBusinessReport && (
          <BusinessReportsPageGenerate
            prompts={prompts}
            fsEntries={fsEntries}
            selectedBusinessReport={selectedBusinessReport}
          />
        )}
      </header>

      <section className="grid flex-grow rounded-lg border border-[#ddd5cb] bg-[#fffdfb] md:grid-cols-[minmax(240px,1fr)_minmax(0,2fr)]">
        <nav aria-label="Templates" className="min-w-0 border-b border-[#e4ddd5] px-5 py-5 md:border-b-0 md:border-r">
          <h2 className="text-base font-semibold text-[#111b35]">Templates</h2>
          {groupedTemplates.map(group => (
            <div key={group.label} className="mt-4">
              <h3 className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                {group.label}
              </h3>
              <ul className="space-y-0.5">
                {group.templates.map(businessReport => (
                  <li key={businessReport.id}>
                    <BusinessReportRow
                      businessReport={businessReport}
                      selectedBusinessReport={selectedBusinessReport}
                      handleTemplateSelect={handleTemplateSelect}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="min-w-0 px-6 py-5">
          {selectedBusinessReport ? (
            <BusinessReportDetails setPrompts={setPrompts} prompts={prompts} businessReport={selectedBusinessReport} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center px-4 py-12 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-[#f1e2ce] text-desert-sand-700">
                <ClipboardList className="size-5" aria-hidden="true" />
              </span>
              <p className="mt-3 text-sm font-semibold text-[#111b35]">No template selected</p>
              <p className="mt-1 text-sm font-medium text-gray-500">Select a template to view details and prompts.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
